import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import { useWebsiteData } from '../../context/WebsiteDataContext';

const AboutNirmiteePage = () => {
  const { getActiveTeamMembers, aboutSettings } = useWebsiteData();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const activeMembers = getActiveTeamMembers ? getActiveTeamMembers() : [];

  const aboutIntro = aboutSettings?.aboutIntro || "Behind this platform is Nirmitee Associates — a design studio driven by creativity, technical expertise, and seamless project coordination. With over 5 years of professional experience in architecture, planning, execution, and liaisoning, the studio was founded by Ar. Mrunal Bhilare and Mr. Akash Patil with a shared vision of delivering thoughtfully planned and efficiently executed projects.";
  const quoteText = aboutSettings?.quoteText || "Together, the founders bring a balanced combination of creative architectural design and efficient project coordination, enabling Nirmitee Associates to deliver projects seamlessly from concept to completion while creating functional, innovative, and practical solutions tailored to client requirements.";
  const quoteAuthor = aboutSettings?.quoteAuthor || "Nirmitee Associates Co-Founders Vision";

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 via-white to-gray-50 text-foreground">
      <Helmet>
        <title>About Us | Nirmitee Associates - Architectural Planning & Liaisoning</title>
        <meta
          name="description"
          content="Welcome to Nirmitee Associates — a design studio and digital plot evaluation platform founded by Ar. Mrunal Bhilare and Mr. Akash Patil. We help builders and developers evaluate plot potential, project feasibility, and profitability with over 5 years of professional experience."
        />
        <meta name="keywords" content="about nirmitee, nirmitee associates, plot feasibility, feasibility calculation, architectural planning, building permissions, mrunal bhilare, akash patil, project profitability, liaisoning pune" />
        <meta property="og:title" content="About Us | Nirmitee Associates - Architectural Planning & Liaisoning" />
        <meta property="og:description" content="Welcome to Nirmitee Associates. Explore our digital plot potential platform and learn about our co-founders Ar. Mrunal Bhilare and Mr. Akash Patil." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="/aboutUs" />
      </Helmet>

      <Header />

      <main className="pt-24 pb-20 sm:pb-32">
        {/* Hero Section */}
        <section className="relative overflow-hidden py-20 lg:py-28">
          {/* Decorative Blueprint Background Grid */}
          <div className="absolute inset-0 blueprint-grid opacity-10 pointer-events-none"></div>
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none"></div>
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-secondary/5 rounded-full blur-3xl pointer-events-none"></div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center space-y-6 max-w-4xl mx-auto">
              <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider">
                <Icon name="Info" size={14} />
                <span>Get to Know Us</span>
              </div>

              <h1 className="font-headline text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-foreground">
                About <span className="text-primary font-extrabold relative inline-block">
                  Nirmitee Associates
                  <span className="absolute left-0 bottom-1 w-full h-2 bg-secondary/30 -z-10 rounded"></span>
                </span>
              </h1>

              <div className="bg-white/80 backdrop-blur-md p-8 sm:p-10 rounded-2xl border border-gray-200/80 shadow-xl max-w-3xl mx-auto transition-all duration-300 hover:shadow-2xl hover:border-primary/20">
                <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed font-body">
                  Welcome to the official website of <strong className="text-foreground font-bold">Nirmitee Associates</strong> — a user-friendly digital platform designed to help builders, developers, and common users easily evaluate the development potential of their plots. The platform enables users to tentatively calculate project feasibility and profitability, making preliminary planning and decision-making more <span className="bg-secondary/20 px-1 py-0.5 rounded text-primary font-bold">accessible, practical, and transparent</span>.
                </p>
              </div>

              {/* Dynamic Badges */}
              <div className="flex flex-wrap justify-center gap-3 pt-4">
                <span className="flex items-center space-x-1 bg-white border border-gray-200 px-4 py-2 rounded-full text-sm font-medium text-gray-700 shadow-sm">
                  <Icon name="Compass" className="text-primary" size={16} />
                  <span>Plot Feasibility</span>
                </span>
                <span className="flex items-center space-x-1 bg-white border border-gray-200 px-4 py-2 rounded-full text-sm font-medium text-gray-700 shadow-sm">
                  <Icon name="Building" className="text-primary" size={16} />
                  <span>Architectural Design</span>
                </span>
                <span className="flex items-center space-x-1 bg-white border border-gray-200 px-4 py-2 rounded-full text-sm font-medium text-gray-700 shadow-sm">
                  <Icon name="FileCheck" className="text-primary" size={16} />
                  <span>Liaisoning & Permissions</span>
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Section 2: Platform Purpose & CTA */}
        <section className="py-16 bg-gray-50 border-y border-gray-100 relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-7 space-y-6">
                <h2 className="font-headline text-3xl font-bold text-foreground">
                  Simplifying Early-Stage Planning
                </h2>

                <p className="text-lg text-muted-foreground leading-relaxed font-body">
                  Our website is developed to simplify the early stages of project understanding by providing basic development insights while also serving as a platform where users can explore our completed and proposed projects. For direct consultations and inquiries, users can easily connect with us through the integrated WhatsApp contact feature.
                </p>

                <div className="flex flex-wrap gap-4 pt-2">
                  <a
                    id="whatsapp-cta-button"
                    href="https://wa.me/918003989393"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 bg-[#25D366] hover:bg-[#20ba5a] text-white px-6 py-3.5 rounded-xl font-cta font-bold shadow-lg shadow-[#25d366]/20 transition-all duration-300 transform hover:-translate-y-0.5"
                  >
                    <Icon name="MessageSquare" size={20} />
                    <span>Inquire on WhatsApp</span>
                  </a>

                  <a
                    id="projects-cta-button"
                    href="/projects"
                    className="inline-flex items-center space-x-2 bg-primary hover:bg-primary/95 text-white px-6 py-3.5 rounded-xl font-cta font-bold shadow-lg shadow-primary/10 transition-all duration-300 transform hover:-translate-y-0.5"
                  >
                    <Icon name="Layers" size={20} />
                    <span>Explore Our Projects</span>
                  </a>
                </div>
              </div>

              <div className="lg:col-span-5">
                <div className="bg-white p-8 rounded-2xl border border-gray-200 shadow-lg space-y-6 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-bl-full pointer-events-none"></div>

                  <h3 className="font-headline text-lg font-bold text-primary flex items-center space-x-2">
                    <Icon name="Activity" />
                    <span>Platform Key Features</span>
                  </h3>

                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-primary/10 rounded flex items-center justify-center text-primary mt-0.5">
                        <Icon name="Calculator" size={16} />
                      </div>
                      <div>
                        <h4 className="font-bold text-foreground text-sm">Tentative Feasibility Feeds</h4>
                        <p className="text-xs text-muted-foreground">Calculate tentative FSI buildability and project potential instantly.</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-primary/10 rounded flex items-center justify-center text-primary mt-0.5">
                        <Icon name="Image" size={16} />
                      </div>
                      <div>
                        <h4 className="font-bold text-foreground text-sm">Interactive Gallery</h4>
                        <p className="text-xs text-muted-foreground">Browse through our completed designs and upcoming proposed plans.</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-primary/10 rounded flex items-center justify-center text-primary mt-0.5">
                        <Icon name="HeartHandshake" size={16} />
                      </div>
                      <div>
                        <h4 className="font-bold text-foreground text-sm">One-Click Consultation</h4>
                        <p className="text-xs text-muted-foreground">Access professional guidance directly with integrated messaging tools.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 3: Behind the Platform & Founders */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
              <h2 className="font-headline text-3xl sm:text-4xl font-bold text-foreground">
                Behind This Platform
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed font-body whitespace-pre-line">
                {aboutIntro}
              </p>
            </div>

            {/* Founder Profile Cards */}
            <div className="grid md:grid-cols-2 gap-8 lg:gap-12 mb-20">
              {activeMembers.map((member) => {
                const expertiseTags = Array.isArray(member.expertise)
                  ? member.expertise
                  : (member.expertise ? String(member.expertise).split(',').map(s => s.trim()).filter(Boolean) : []);

                return (
                  <div key={member.id} className="bg-white rounded-3xl border border-gray-200 shadow-lg hover:shadow-2xl transition-all duration-500 flex flex-col overflow-hidden group pt-10 text-center">
                    {/* Photo container (rounded circular) */}
                    <div className="relative mx-auto w-40 h-40 rounded-full overflow-hidden border-4 border-primary/10 group-hover:border-primary/30 transition-all duration-500 shadow-md">
                      <img
                        src={member.image}
                        alt={member.name}
                        className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700 ease-out"
                      />
                    </div>

                    {/* Content */}
                    <div className="p-8 flex flex-col justify-between flex-grow space-y-6">
                      <div className="space-y-4">
                        <div>
                          <span className="inline-block bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider px-3.5 py-1.5 rounded-full font-cta mb-3">
                            {member.position}
                          </span>
                          <h3 className="font-headline text-2xl font-bold text-foreground group-hover:text-primary transition-colors">
                            {member.name}
                          </h3>
                        </div>
                        <p className="text-base text-muted-foreground leading-relaxed font-body">
                          {member.bio}
                        </p>
                      </div>

                      {expertiseTags.length > 0 && (
                        <div className="pt-6 border-t border-gray-100 flex flex-col items-center">
                          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-3 font-cta">Expertise Fields</span>
                          <div className="flex flex-wrap justify-center gap-2">
                            {expertiseTags.map((tag, idx) => (
                              <span key={idx} className="bg-primary/5 text-primary text-xs font-medium px-3 py-1.5 rounded-lg border border-primary/10">
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Joint Vision Statement */}
            <div className="bg-gradient-to-br from-[#db945c] via-[#7d3d0b]/95 to-[#7d3d0b]/90 text-primary-foreground p-8 sm:p-12 rounded-3xl shadow-xl relative overflow-hidden text-center max-w-5xl mx-auto">
              <div className="absolute inset-0 blueprint-grid opacity-10 pointer-events-none"></div>

              <div className="relative z-10 space-y-6 max-w-4xl mx-auto">
                <div className="flex justify-center text-secondary/80">
                  <Icon name="Quote" size={48} />
                </div>

                <h3 className="font-headline text-xl sm:text-2xl font-bold italic leading-relaxed text-white">
                  "{quoteText}"
                </h3>

                <div className="h-1 w-16 bg-secondary/60 mx-auto rounded-full"></div>
                <p className="text-xs font-bold uppercase tracking-wider text-secondary/90">
                  {quoteAuthor}
                </p>
              </div>
            </div>

          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AboutNirmiteePage;